import { useCallback, useEffect, useId, useMemo, useState, type FormEvent } from 'react';

import { extractBsdsInputValue } from '../../utils/bsdsInputUtils';

const DEFAULT_DEBOUNCE_MS = 400;

export type UseVidaTableListControlsParams<T extends Record<string, string>> = {
  emptyFilters: T;
  loadedRowCount: number;
  debounceMs?: number;
  resetPanelFiltersDeps?: readonly unknown[];
};

export type UseVidaTableListControlsReturn<T extends Record<string, string>> = {
  listSearchInputValue: string;
  setListSearchInputValue: (value: string) => void;
  debouncedSearch: string;
  sortBy: string;
  setSortBy: (value: string) => void;
  showFilters: boolean;
  setShowFilters: (value: boolean) => void;
  toggleFilters: () => void;
  panelFilters: T;
  appliedPanelFilters: T | null;
  updatePanelFilter: (key: keyof T, value: string) => void;
  applyPanelFilters: () => void;
  clearPanelFilters: () => void;
  resetPanelFilters: () => void;
  resetAll: () => void;
  hasPanelFilterDraft: boolean;
  isPanelFilterActive: boolean;
  isPanelFilterApplyEnabled: boolean;
  listSearchInputId: string;
  filtersPanelId: string;
  handleSearchInput: (event: FormEvent<HTMLBsdsInputElement>) => void;
};

export function useVidaTableListControls<T extends Record<string, string>>({
  emptyFilters,
  loadedRowCount,
  debounceMs = DEFAULT_DEBOUNCE_MS,
  resetPanelFiltersDeps = [],
}: UseVidaTableListControlsParams<T>): UseVidaTableListControlsReturn<T> {
  const listSearchInputId = useId();
  const filtersPanelId = useId();
  const [listSearchInputValue, setListSearchInputValue] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sortBy, setSortBy] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [panelFilters, setPanelFilters] = useState<T>(emptyFilters);
  const [appliedPanelFilters, setAppliedPanelFilters] = useState<T | null>(null);

  const resetPanelFilters = useCallback(() => {
    setPanelFilters(emptyFilters);
    setAppliedPanelFilters(null);
    setShowFilters(false);
  }, [emptyFilters]);

  const resetAll = useCallback(() => {
    setListSearchInputValue('');
    setDebouncedSearch('');
    setSortBy('');
    setPanelFilters(emptyFilters);
    setAppliedPanelFilters(null);
    setShowFilters(false);
  }, [emptyFilters]);

  const updatePanelFilter = useCallback((key: keyof T, value: string) => {
    setPanelFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const applyPanelFilters = useCallback(() => {
    setAppliedPanelFilters({ ...panelFilters });
  }, [panelFilters]);

  const clearPanelFilters = useCallback(() => {
    setPanelFilters(emptyFilters);
    setAppliedPanelFilters(null);
  }, [emptyFilters]);

  const toggleFilters = useCallback(() => {
    setShowFilters((open) => !open);
  }, []);

  const handleSearchInput = useCallback((event: FormEvent<HTMLBsdsInputElement>) => {
    setListSearchInputValue(extractBsdsInputValue(event));
  }, []);

  useEffect(() => {
    if (debounceMs <= 0) {
      setDebouncedSearch(listSearchInputValue);
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setDebouncedSearch(listSearchInputValue);
    }, debounceMs);

    return () => window.clearTimeout(timeoutId);
  }, [listSearchInputValue, debounceMs]);

  const searchResetValue = debounceMs <= 0 ? listSearchInputValue : debouncedSearch;

  useEffect(() => {
    resetPanelFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- resetPanelFiltersDeps are caller-defined reset triggers
  }, [searchResetValue, ...resetPanelFiltersDeps]);

  const hasPanelFilterDraft = useMemo(
    () => Object.values(panelFilters).some((value) => value.trim() !== ''),
    [panelFilters],
  );

  const isPanelFilterActive = useMemo(
    () => appliedPanelFilters !== null
      && Object.values(appliedPanelFilters).some((value) => value.trim() !== ''),
    [appliedPanelFilters],
  );

  const isPanelFilterApplyEnabled = loadedRowCount > 0;

  return {
    listSearchInputValue,
    setListSearchInputValue,
    debouncedSearch,
    sortBy,
    setSortBy,
    showFilters,
    setShowFilters,
    toggleFilters,
    panelFilters,
    appliedPanelFilters,
    updatePanelFilter,
    applyPanelFilters,
    clearPanelFilters,
    resetPanelFilters,
    resetAll,
    hasPanelFilterDraft,
    isPanelFilterActive,
    isPanelFilterApplyEnabled,
    listSearchInputId,
    filtersPanelId,
    handleSearchInput,
  };
}
